import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function Select({ value, onValueChange, children, placeholder, className, disabled }: SelectProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent): void {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedLabel = React.useMemo(() => {
    const childrenArr = React.Children.toArray(children) as React.ReactElement<SelectItemProps>[];
    const found = childrenArr.find(c => c.props?.value === value);
    return found?.props?.children ?? placeholder ?? 'Select...';
  }, [children, value, placeholder]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          !value && 'text-muted-foreground',
        )}
        onClick={() => setOpen(!open)}
      >
        <span>{selectedLabel}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return null;
            return React.cloneElement(child as React.ReactElement<SelectItemProps>, {
              onSelect: (val: string) => {
                onValueChange(val);
                setOpen(false);
              },
              _selected: (child.props as SelectItemProps).value === value,
            });
          })}
        </div>
      )}
    </div>
  );
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  onSelect?: (value: string) => void;
  _selected?: boolean;
  disabled?: boolean;
}

function SelectItem({ value, children, onSelect, _selected, disabled }: SelectItemProps): React.ReactElement {
  return (
    <div
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
        _selected && 'bg-accent text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
      )}
      onClick={() => !disabled && onSelect?.(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !disabled) {
          onSelect?.(value);
        }
      }}
      role="option"
      aria-selected={_selected}
      tabIndex={0}
    >
      {children}
    </div>
  );
}

export { Select, SelectItem };
